import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CharacterRig } from '@/components/CharacterRig';

// Authenticated cached audio only. No speech synthesis, capture or automatic play.
export function PartnerPregamePlayer({ episode, loadAudio }) {
  const [active, setActive] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');
  const epoch = useRef(0);
  const current = useRef(null);
  const abort = useRef(null);
  const mounted = useRef(true);
  const dispose = useCallback(() => {
    epoch.current += 1;
    abort.current?.abort(); abort.current = null;
    const item = current.current; current.current = null;
    if (item) {
      item.audio.onended = null; item.audio.onerror = null; item.audio.ontimeupdate = null;
      try { item.audio.pause(); item.audio.removeAttribute('src'); item.audio.load(); } catch (_) { /* detached */ }
      URL.revokeObjectURL(item.url);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    dispose(); setActive(-1); setPlaying(false); setLevel(0); setError('');
    return () => { mounted.current = false; dispose(); };
  }, [episode, loadAudio, dispose]);

  const stop = () => { dispose(); setActive(-1); setPlaying(false); setLevel(0); };
  const play = async (index) => {
    dispose(); const generation = epoch.current;
    if (!episode?.lines?.[index] || episode?.audio?.state !== 'ready') { stop(); return; }
    const metadata = episode.audio.lines.find(l => l.index === episode.lines[index].index);
    if (!metadata) { setError('Recorded audio is incomplete.'); stop(); return; }
    const controller = new AbortController(); abort.current = controller;
    setError(''); setPlaying(true); setActive(index); setLevel(0);
    try {
      const blob = await loadAudio(episode.audio_id, metadata.index, controller.signal);
      if (!mounted.current || generation !== epoch.current) return;
      const url = URL.createObjectURL(blob); const audio = new Audio(url);
      current.current = { audio, url };
      const valid = () => mounted.current && generation === epoch.current;
      audio.ontimeupdate = () => {
        if (!valid()) return;
        const env = metadata.env || [];
        const pos = Math.min(env.length - 1, Math.floor(audio.currentTime * 1000 / metadata.duration_ms * env.length));
        setLevel(Number.isFinite(env[pos]) ? Math.min(1, Math.max(0, env[pos])) : 0);
      };
      audio.onerror = () => { if (valid()) { setError('Recorded audio could not play.'); stop(); } };
      audio.onended = () => { if (valid()) { if (index + 1 < episode.lines.length) void play(index + 1); else stop(); } };
      await audio.play();
      if (!valid()) { try { audio.pause(); } catch (_) { /* detached */ } }
    } catch (_) {
      if (mounted.current && generation === epoch.current) { setError('Recorded audio could not play.'); stop(); }
    }
  };
  const line = episode?.lines?.[active];
  return <section aria-label="Partner pregame recorded show">
    <p>{episode?.disclaimer}</p><p>Source snapshot: {episode?.source_date}. Not a live feed.</p>
    <div className="grid grid-cols-3 gap-3">
      {['bruce', 'alpha_moose', 'cow_pie'].map(id => <CharacterRig key={id} id={id}
        width={220} reduced={false} speaking={playing && line?.speaker === id}
        playing={playing} level={line?.speaker === id ? level : 0} />)}
    </div>
    <p aria-live="polite">{line?.caption || line?.text || 'Ready for playback'}</p>
    {error && <p role="alert">{error}</p>}
    <button disabled={episode?.audio?.state !== 'ready' || playing} onClick={() => void play(0)}>Play recorded show</button>
    <button disabled={!playing} onClick={stop}>Stop playback</button>
    {episode?.audio?.state !== 'ready' && <p>Recorded audio is not available yet.</p>}
  </section>;
}
